import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorBoundary, installGlobalErrorReporters } from './components/ErrorBoundary';
import { trackPageView } from './lib/analytics';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import BookmarkDetail from './pages/BookmarkDetail';
import AIChatPage from './pages/AIChatPage';
import AuthCallback from './pages/AuthCallback';
import SharePage from './pages/SharePage';
import NotFoundPage from './pages/NotFoundPage';
import AdminPage from './pages/AdminPage';
import AdminCuratorPage from './pages/AdminCuratorPage';
import AdminUserDetail from './pages/AdminUserDetail';
import JoinCategoryPage from './pages/JoinCategoryPage';
import PublicCategoryPage from './pages/PublicCategoryPage';
import PublicProfilePage from './pages/PublicProfilePage';
import BillingPage from './pages/BillingPage';
import SettingsPage from './pages/SettingsPage';
import PlayFeatureGraphic from './pages/PlayFeatureGraphic';
import TryItFlow from './pages/TryItFlow';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import PlayScreenshots from './pages/PlayScreenshots';
import PlayProcessingPreview from './pages/PlayProcessingPreview';
import AboutPage from './pages/AboutPage';
import SupportPage from './pages/SupportPage';
import HowItWorksPage from './pages/HowItWorksPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BiometricGate from './components/BiometricGate';
import { PushPermissionPrompt } from './components/PushPermissionPrompt';
import './App.css';

// GA4 SPA route-change tracker. The initial page_view is fired by gtag's
// auto-config in `index.html`; this hook captures every subsequent
// react-router navigation so /dashboard, /c/{id}, /share/{id} etc.
// each show up as distinct page_views in GA4.
function RouteChangeTracker() {
    const location = useLocation();
    const isFirstRender = React.useRef(true);
    React.useEffect(() => {
        // Skip the initial render — gtag's `send_page_view: true` in
        // index.html already covered it. Without this guard we'd
        // double-count the landing page_view.
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        trackPageView(location.pathname + location.search);
    }, [location.pathname, location.search]);
    return null;
}

function App() {
    useEffect(() => {
        // Install global JS error + unhandled-rejection reporters so
        // the ErrorBoundary isn't the only path to an error report.
        installGlobalErrorReporters();
        const removeBadge = () => {
            const el = document.getElementById('emergent-badge');
            if (el) el.remove();
        };
        removeBadge();
        const iv = setInterval(removeBadge, 300);
        return () => clearInterval(iv);
    }, []);

    return (
        <ErrorBoundary>
            <BrowserRouter>
                <AuthProvider>
                    <RouteChangeTracker />
                    <BiometricGate>
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/try" element={<TryItFlow />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/bookmark/:id" element={<BookmarkDetail />} />
                        {/* /search is the canonical route; /ai-chat is
                            kept as an alias so any bookmarked URLs, TWA
                            share targets, or in-flight sessions from
                            the old naming continue to work. */}
                        <Route path="/search" element={<AIChatPage />} />
                        <Route path="/ai-chat" element={<AIChatPage />} />
                        <Route path="/share/:bookmarkId" element={<SharePage />} />
                        <Route path="/auth/google" element={<AuthCallback />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/admin" element={<AdminPage />} />
                        <Route path="/admin/curator" element={<AdminCuratorPage />} />
                        <Route path="/admin/users/:userId" element={<AdminUserDetail />} />
                        <Route path="/join/:shareToken" element={<JoinCategoryPage />} />
                        <Route path="/c/:categoryId" element={<PublicCategoryPage />} />
                        <Route path="/account/billing" element={<BillingPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/promo/feature-graphic" element={<PlayFeatureGraphic />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/support" element={<SupportPage />} />
                        <Route path="/how-it-works" element={<HowItWorksPage />} />
                        <Route path="/promo/screenshots" element={<PlayScreenshots />} />
                        <Route path="/promo/processing" element={<PlayProcessingPreview />} />
                        {/* Public profile route - MUST be last before the 404
                            catch-all because it greedily matches any
                            top-level path. The PublicProfilePage component
                            renders a 404 view for unknown usernames so the
                            UX stays consistent. */}
                        <Route path="/:username" element={<PublicProfilePage />} />
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    </BiometricGate>
                    <PushPermissionPrompt />
                </AuthProvider>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
